import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InterventionRoomControllerApi } from '../api/generated';
import type { InterventionRoomResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';

export interface UpdateInterventionRoomPayload {
  description: string;
  capacityLimit: number | undefined;
  interventionRoomPropertyIds: string[];
}

export interface UseInterventionRoomsResult {
  rooms: InterventionRoomResponse[];
  refresh: () => Promise<void>;
  createRoom: (description: string) => Promise<void>;
  updateRoom: (roomId: string, payload: UpdateInterventionRoomPayload) => Promise<void>;
  deleteRoom: (roomId: string) => Promise<void>;
}

export function useInterventionRooms(locationId: string | null): UseInterventionRoomsResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [rooms, setRooms] = useState<InterventionRoomResponse[]>([]);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    try {
      const api = new InterventionRoomControllerApi(createApiConfiguration());
      const response = await api.listInterventionRooms({ locationId });
      setRooms(response.data);
    } catch {
      dispatch(showError({ message: t('fetch-error') }));
    }
  }, [locationId, dispatch, t]);

  useEffect(() => {
    if (!locationId) return;
    void refresh();
  }, [locationId, refresh]);

  const createRoom = useCallback(
    async (description: string) => {
      if (!locationId) return;
      try {
        const api = new InterventionRoomControllerApi(createApiConfiguration());
        await api.createInterventionRoom({
          locationId,
          createInterventionRoomRequest: {
            description,
            interventionRoomPropertyIds: []
          }
        });
        await refresh();
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, refresh, dispatch, t]
  );

  const updateRoom = useCallback(
    async (roomId: string, payload: UpdateInterventionRoomPayload) => {
      if (!locationId) return;
      try {
        const api = new InterventionRoomControllerApi(createApiConfiguration());
        await api.updateInterventionRoom({
          locationId,
          interventionRoomId: roomId,
          updateInterventionRoomRequest: payload
        });
        await refresh();
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, refresh, dispatch, t]
  );

  const deleteRoom = useCallback(
    async (roomId: string) => {
      if (!locationId) return;
      try {
        const api = new InterventionRoomControllerApi(createApiConfiguration());
        await api.deleteInterventionRoom({ locationId, interventionRoomId: roomId });
        await refresh();
      } catch {
        dispatch(showError({ message: t('delete-intervention-room-error') }));
      }
    },
    [locationId, refresh, dispatch, t]
  );

  return { rooms, refresh, createRoom, updateRoom, deleteRoom };
}
